import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const SubscriptionManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [activeTab, setActiveTab] = useState('subscriptions');

  const queryClient = useQueryClient();

  // Récupération des abonnements
  const { data: subscriptions, isLoading: subscriptionsLoading } = useQuery(
    ['super-admin-subscriptions', searchTerm, statusFilter, planFilter],
    () => axios.get(`/api/super-admin/subscriptions?search=${searchTerm}&status=${statusFilter}&plan=${planFilter}`).then(res => res.data.subscriptions || res.data),

  );

  // Récupération des plans d'abonnement
  const { data: plans, isLoading: plansLoading } = useQuery(
    'super-admin-plans',
    () => axios.get('/api/super-admin/plans').then(res => Array.isArray(res.data) ? res.data : res.data.plans || []),

  );

  // Récupération des paiements
  const { data: payments, isLoading: paymentsLoading } = useQuery(
    'super-admin-payments',
    () => axios.get('/api/super-admin/payments').then(res => res.data.payments || res.data),
    
  );

  // Mutations pour les plans
  const createPlanMutation = useMutation(
    (planData) => axios.post('/api/super-admin/plans', planData),
    {
      onSuccess: () => {
        toast.success('Plan créé avec succès');
        queryClient.invalidateQueries('super-admin-plans');
        setShowCreatePlanModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      }
    }
  );

  const updatePlanMutation = useMutation(
    ({ planId, planData }) => axios.put(`/api/super-admin/plans/${planId}`, planData),
    {
      onSuccess: () => {
        toast.success('Plan modifié avec succès');
        queryClient.invalidateQueries('super-admin-plans');
        setShowEditPlanModal(false);
        setEditingPlan(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );

  const deletePlanMutation = useMutation(
    (planId) => axios.delete(`/api/super-admin/plans/${planId}`),
    {
      onSuccess: () => {
        toast.success('Plan supprimé avec succès');
        queryClient.invalidateQueries('super-admin-plans');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  );

  // Mutations pour les abonnements
  const cancelSubscriptionMutation = useMutation(
    (subscriptionId) => axios.post(`/api/super-admin/subscriptions/${subscriptionId}/cancel`),
    {
      onSuccess: () => {
        toast.success('Abonnement annulé avec succès');
        queryClient.invalidateQueries('super-admin-subscriptions');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'annulation');
      }
    }
  );

  const refundPaymentMutation = useMutation(
    ({ paymentId, amount, reason }) => axios.post(`/api/super-admin/payments/${paymentId}/refund`, { amount, reason }),
    {
      onSuccess: () => {
        toast.success('Remboursement effectué avec succès');
        queryClient.invalidateQueries('super-admin-payments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du remboursement');
      }
    }
  );

  const statuses = [
    { value: 'active', label: 'Actif', color: 'green' },
    { value: 'cancelled', label: 'Annulé', color: 'red' },
    { value: 'expired', label: 'Expiré', color: 'gray' },
    { value: 'pending', label: 'En attente', color: 'yellow' }
  ];

  const paymentStatuses = [
    { value: 'completed', label: 'Complété', color: 'green' },
    { value: 'pending', label: 'En attente', color: 'yellow' },
    { value: 'failed', label: 'Échoué', color: 'red' },
    { value: 'refunded', label: 'Remboursé', color: 'blue' }
  ];

  const handleSelectSubscription = (subscription) => {
    setSelectedSubscriptions(prev => 
      prev.includes(subscription.id) 
        ? prev.filter(id => id !== subscription.id)
        : [...prev, subscription.id]
    );
  };

  const handleSelectAll = () => {
    if (selectedSubscriptions.length === subscriptions?.length) {
      setSelectedSubscriptions([]);
    } else {
      setSelectedSubscriptions(subscriptions?.map(sub => sub.id) || []);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowEditPlanModal(true);
  };

  const handleDeletePlan = (planId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce plan ?')) {
      deletePlanMutation.mutate(planId);
    }
  };

  const handleCancelSubscription = (subscriptionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cet abonnement ?')) {
      cancelSubscriptionMutation.mutate(subscriptionId);
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentDetails(true);
  };

  const handleRefundPayment = (paymentId) => {
    const amount = prompt('Montant du remboursement (laisser vide pour remboursement total):');
    const reason = prompt('Raison du remboursement:');
    if (reason !== null) {
      refundPaymentMutation.mutate({ paymentId, amount: amount || null, reason });
    }
  };

  const getStatusBadge = (status, type = 'subscription') => {
    const statusList = type === 'payment' ? paymentStatuses : statuses;
    const statusConfig = statusList.find(s => s.value === status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusConfig?.color}-100 text-${statusConfig?.color}-800`}>
        {statusConfig?.label}
      </span>
    );
  };

  const CreatePlanModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouveau plan</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const planData = {
              name: formData.get('name'),
              description: formData.get('description'),
              price: parseFloat(formData.get('price')),
              currency: formData.get('currency'),
              duration: parseInt(formData.get('duration')),
              features: formData.get('features').split('\n').filter(f => f.trim()),
              maxProjects: parseInt(formData.get('maxProjects')) || null,
              maxContacts: parseInt(formData.get('maxContacts')) || null,
              priority: formData.get('priority') === 'true'
            };
            createPlanMutation.mutate(planData);
          }}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom du plan</label>
                <input type="text" name="name" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows="3" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prix</label>
                  <input type="number" name="price" step="0.01" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Devise</label>
                  <select name="currency" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="XOF">XOF</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Durée (jours)</label>
                <input type="number" name="duration" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fonctionnalités (une par ligne)</label>
                <textarea name="features" rows="4" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Accès aux investisseurs&#10;Support prioritaire&#10;Analytics avancées"></textarea>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max projets</label>
                  <input type="number" name="maxProjects" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Illimité si vide" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max contacts</label>
                  <input type="number" name="maxContacts" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Illimité si vide" />
                </div>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input type="checkbox" name="priority" className="rounded border-gray-300 text-primary focus:ring-primary" />
                  <span className="ml-2 text-sm text-gray-700">Support prioritaire</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreatePlanModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createPlanMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {createPlanMutation.isLoading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const EditPlanModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Modifier le plan</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const planData = {
              name: formData.get('name'),
              description: formData.get('description'),
              price: parseFloat(formData.get('price')),
              currency: formData.get('currency'),
              duration: parseInt(formData.get('duration')),
              features: formData.get('features').split('\n').filter(f => f.trim()),
              maxProjects: parseInt(formData.get('maxProjects')) || null,
              maxContacts: parseInt(formData.get('maxContacts')) || null,
              priority: formData.get('priority') === 'true'
            };
            updatePlanMutation.mutate({ planId: editingPlan.id, planData });
          }}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom du plan</label>
                <input type="text" name="name" defaultValue={editingPlan?.name} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows="3" defaultValue={editingPlan?.description} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prix</label>
                  <input type="number" name="price" step="0.01" defaultValue={editingPlan?.price} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Devise</label>
                  <select name="currency" defaultValue={editingPlan?.currency} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="XOF">XOF</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Durée (jours)</label>
                <input type="number" name="duration" defaultValue={editingPlan?.duration} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fonctionnalités (une par ligne)</label>
                <textarea name="features" rows="4" defaultValue={editingPlan?.features?.join('\n')} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max projets</label>
                  <input type="number" name="maxProjects" defaultValue={editingPlan?.maxProjects} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max contacts</label>
                  <input type="number" name="maxContacts" defaultValue={editingPlan?.maxContacts} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input type="checkbox" name="priority" defaultChecked={editingPlan?.priority} className="rounded border-gray-300 text-primary focus:ring-primary" />
                  <span className="ml-2 text-sm text-gray-700">Support prioritaire</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowEditPlanModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={updatePlanMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {updatePlanMutation.isLoading ? 'Modification...' : 'Modifier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const PaymentDetailsModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Détails du paiement</h3>
            <button
              onClick={() => setShowPaymentDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Fermer</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {selectedPayment && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Informations du paiement</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">ID:</span> {selectedPayment.id}</p>
                    <p><span className="font-medium">Montant:</span> {selectedPayment.amount} {selectedPayment.currency}</p>
                    <p><span className="font-medium">Statut:</span> {getStatusBadge(selectedPayment.status, 'payment')}</p>
                    <p><span className="font-medium">Méthode:</span> {selectedPayment.paymentMethod}</p>
                    <p><span className="font-medium">Date:</span> {new Date(selectedPayment.createdAt).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Utilisateur</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">Nom:</span> {selectedPayment.userName}</p>
                    <p><span className="font-medium">Email:</span> {selectedPayment.userEmail}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Plan d'abonnement</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">Plan:</span> {selectedPayment.planName}</p>
                    <p><span className="font-medium">Durée:</span> {selectedPayment.planDuration} jours</p>
                  </div>
                </div>
                
                {selectedPayment.status === 'completed' && (
                  <div>
                    <h4 className="font-medium text-gray-900">Actions</h4>
                    <div className="mt-2">
                      <button
                        onClick={() => handleRefundPayment(selectedPayment.id)}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                      >
                        Rembourser
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un abonnement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Tous les statuts</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Tous les plans</option>
            {plans?.map(plan => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </select>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {subscriptions?.length || 0} abonnement(s)
            </span>
          </div>
        </div>
      </div>

      {/* Tableau des abonnements */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedSubscriptions.length === subscriptions?.length && subscriptions?.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date d'expiration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions?.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedSubscriptions.includes(subscription.id)}
                      onChange={() => handleSelectSubscription(subscription)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-700">
                            {subscription.userName?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {subscription.userName}
                        </div>
                        <div className="text-sm text-gray-500">{subscription.userEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {subscription.planName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(subscription.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {subscription.price} {subscription.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(subscription.expiresAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {subscription.status === 'active' && (
                        <button
                          onClick={() => handleCancelSubscription(subscription.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Annuler"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Plans d'abonnement</h3>
        <button
          onClick={() => setShowCreatePlanModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouveau plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <div key={plan.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                <p className="text-gray-600 text-sm">{plan.description}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditPlan(plan)}
                  className="text-indigo-600 hover:text-indigo-900"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="text-3xl font-bold text-gray-900">
                {plan.price} {plan.currency}
              </div>
              <div className="text-sm text-gray-500">
                pour {plan.duration} jours
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              {plan.features?.map((feature, index) => (
                <div key={index} className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  {feature}
                </div>
              ))}
            </div>
            
            <div className="text-xs text-gray-500">
              {plan.maxProjects ? `Max ${plan.maxProjects} projets` : 'Projets illimités'} • 
              {plan.maxContacts ? ` Max ${plan.maxContacts} contacts` : ' Contacts illimités'}
              {plan.priority && ' • Support prioritaire'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Paiements récents</h3>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments?.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {payment.id.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.userName}</div>
                    <div className="text-sm text-gray-500">{payment.userEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.planName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.amount} {payment.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payment.status, 'payment')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(payment.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewPayment(payment)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (subscriptionsLoading || plansLoading || paymentsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des abonnements</h1>
              <p className="mt-2 text-gray-600">
                Gérez les plans d'abonnement, les abonnements et les paiements
              </p>
            </div>
            <button
              onClick={() => queryClient.invalidateQueries(['super-admin-plans', 'super-admin-subscriptions', 'super-admin-payments'])}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Actualiser
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'subscriptions', label: 'Abonnements', count: subscriptions?.length || 0 },
                { id: 'plans', label: 'Plans', count: plans?.length || 0 },
                { id: 'payments', label: 'Paiements', count: payments?.length || 0 }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'subscriptions' && renderSubscriptions()}
        {activeTab === 'plans' && renderPlans()}
        {activeTab === 'payments' && renderPayments()}
      </div>

      {/* Modales */}
      {showCreatePlanModal && <CreatePlanModal />}
      {showEditPlanModal && <EditPlanModal />}
      {showPaymentDetails && <PaymentDetailsModal />}
    </div>
  );
};

export default SubscriptionManagement;



